/**
 * Campaign Progress Screen - Screen 4
 *
 * Real-time progress monitoring interface for autonomous campaigns.
 *
 * Design Pattern: Simplified dashboard with real-time SSE updates
 * - Top Bar: Status and controls
 * - Metric Cards: Sourced, Evaluated, Contacted, Scheduled
 * - Progress Bar: Overall progress
 * - Current Phase: Phase status and progress
 * - Activity Feed: Real-time events
 *
 * Route: /campaigns/autonomous/:session_id/progress
 */

import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Mail,
  MessageCircle,
  Pause,
  Phone,
  Play,
  Settings,
  Target,
  Users,
  AlertCircle,
  Calendar,
  Download,
  Plus
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';

function CampaignProgress({ previewMode = false }) {
  const { session_id } = useParams();
  const navigate = useNavigate();

  // Campaign state
  const [campaignStatus, setCampaignStatus] = useState('executing'); // 'executing' | 'paused' | 'completed'
  const [currentPhase, setCurrentPhase] = useState(2);
  const [totalPhases, setTotalPhases] = useState(4);
  const [goalId, setGoalId] = useState(null);

  // Progress metrics
  const [metrics, setMetrics] = useState({
    sourced: 0,
    evaluated: 0,
    contacted: 0,
    scheduled: 0
  });

  // Phase status
  const [phaseStatus, setPhaseStatus] = useState({
    source: 'done',      // 'done' | 'active' | 'next'
    evaluate: 'active',
    outreach: 'next',
    schedule: 'next'
  });

  // Overall progress
  const [progress, setProgress] = useState({
    percentage: 0,
    goal: 10,
    current: 0,
    timeRemaining: '20 min',
    timeElapsed: '15 min'
  });

  // Current phase details
  const [currentPhaseDetails, setCurrentPhaseDetails] = useState({
    name: 'Evaluation',
    progress: 60,
    description: 'Evaluating candidates...'
  });

  // Activity feed
  const [activityFeed, setActivityFeed] = useState([]);

  // SSE connection
  const eventSourceRef = useRef(null);
  const activityFeedEndRef = useRef(null);

  // Preview mode: Show sample data
  const isPreview = previewMode || !session_id;

  // Sample data for preview
  const sampleMetrics = {
    sourced: 52,
    evaluated: 18,
    contacted: 8,
    scheduled: 2
  };

  const sampleActivityFeed = [
    { type: 'email', message: 'Email sent: John Doe', timestamp: '2 minutes ago', icon: Mail },
    { type: 'evaluated', message: 'Evaluated: Jane Smith (85)', timestamp: '1 minute ago', icon: CheckCircle2 },
    { type: 'whatsapp', message: 'WhatsApp: Bob Johnson', timestamp: '30 seconds ago', icon: MessageCircle },
    { type: 'scheduled', message: 'Scheduled: Alice Brown', timestamp: 'Just now', icon: Calendar }
  ];

  // Initialize preview mode
  useEffect(() => {
    if (isPreview) {
      setMetrics(sampleMetrics);
      setProgress({
        percentage: 45,
        goal: 10,
        current: 2,
        timeRemaining: '20 min',
        timeElapsed: '15 min'
      });
      setActivityFeed(sampleActivityFeed);
      setCurrentPhaseDetails({
        name: 'Evaluation',
        progress: 60,
        description: 'Evaluating candidates...'
      });
      return;
    }

    // Load goal_id from localStorage or resolve from session API
    const loadGoalId = async () => {
      try {
        let resolvedGoalId = localStorage.getItem(`goal_id_${session_id}`);
        if (!resolvedGoalId) {
          // Fallback: resolve goal_id from session
          const res = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/session/${session_id}/goal`);
          if (res.ok) {
            const data = await res.json();
            resolvedGoalId = data.goal_id;
            if (resolvedGoalId) {
              localStorage.setItem(`goal_id_${session_id}`, resolvedGoalId);
            }
          }
        }
        if (resolvedGoalId) {
          setGoalId(resolvedGoalId);
          connectToSSE(resolvedGoalId);
        } else {
          setMetrics(sampleMetrics);
          setActivityFeed(sampleActivityFeed);
        }
      } catch (error) {
        console.error('Error loading goal_id:', error);
      }
    };

    if (session_id) {
      loadGoalId();
    }

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [session_id, isPreview]);

  // Connect to SSE stream
  const connectToSSE = (goalId) => {
    if (!goalId) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const eventSource = new EventSource(`${API_BASE_URL}/api/campaigns/autonomous/${goalId}/stream`);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle different event types
        if (data.event === 'connected') {
          console.log('SSE connected for goal:', data.goal_id);
        } else if (data.event === 'initial_progress') {
          updateProgressFromData(data.progress, data.progress_percentage);
          if (data.activity_log?.length) {
            setActivityFeed(data.activity_log.map((e) => ({
              type: e.phase || 'info',
              message: e.message,
              timestamp: e.timestamp ? new Date(e.timestamp).toLocaleTimeString() : '',
              icon: getActivityIcon(e.phase)
            })).reverse());
          }
        } else if (data.event === 'activity') {
          addActivityFromLog(data.phase, data.message, data.details, data.timestamp);
        } else if (data.progress) {
          // SSE sends { goal_id, timestamp, progress: { progress, progress_percentage, phase, metrics } }
          const payload = data.progress;
          const counts = payload.progress || payload;
          const pct = payload.progress_percentage ?? data.progress_percentage;
          const phase = payload.phase || data.phase;
          const metrics = payload.metrics || data.metrics || {};

          updateProgressFromData(counts, pct);

          // Add to activity feed when we have a phase update
          if (phase) {
            addActivityItem(phase, metrics);
          }
        } else if (data.event === 'phase_complete') {
          // Phase completed
          handlePhaseComplete(data.phase, data.result);
        } else if (data.event === 'goal_achieved') {
          // Campaign complete
          setCampaignStatus('completed');
          addActivityItem('complete', { message: 'Campaign completed successfully!' });
          // Optionally navigate to results after a delay
          setTimeout(() => {
            if (session_id) {
              navigate(`/campaigns/autonomous/${session_id}/results`);
            }
          }, 3000); // Navigate after 3 seconds
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      // Attempt to reconnect after delay
      setTimeout(() => {
        if (goalId) {
          connectToSSE(goalId);
        }
      }, 5000);
    };
  };

  // Update progress from SSE data
  const updateProgressFromData = (progressData, percentage) => {
    if (progressData) {
      setMetrics({
        sourced: progressData.sourced_count || 0,
        evaluated: progressData.evaluated_count || 0,
        contacted: progressData.contacted_count || 0,
        scheduled: progressData.scheduled_count || 0
      });

      setProgress(prev => ({
        ...prev,
        percentage: percentage || prev.percentage,
        current: progressData.scheduled_count || prev.current
      }));
    }
  };

  // Add activity item from phase + metrics (legacy)
  const addActivityItem = (phase, metrics) => {
    addActivityFromLog(phase, formatActivityMessage(phase, metrics), metrics, null);
  };

  // Add activity from rich log entry (where searched, evaluation, etc.)
  const addActivityFromLog = (phase, message, details, timestamp) => {
    const activityItem = {
      type: phase || 'info',
      message: message || 'Activity',
      timestamp: timestamp ? new Date(timestamp).toLocaleTimeString() : 'Just now',
      icon: getActivityIcon(phase),
      details
    };

    setActivityFeed(prev => {
      const newFeed = [activityItem, ...prev].slice(0, 20);
      return newFeed;
    });

    setTimeout(() => {
      activityFeedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Format activity message
  const formatActivityMessage = (phase, metrics) => {
    switch (phase) {
      case 'source':
        return `Sourced ${metrics.candidates_filtered || metrics.sourced_count || 0} candidates`;
      case 'evaluate':
        return `Evaluated ${metrics.evaluated_count || 0} candidates`;
      case 'outreach':
        return `Contacted ${metrics.contacted_count || 0} candidates`;
      case 'schedule':
        return `Scheduled ${metrics.scheduled_count || 0} interviews`;
      default:
        return `Phase ${phase} updated`;
    }
  };

  // Get activity icon
  const getActivityIcon = (phase) => {
    switch (phase) {
      case 'source':
        return Users;
      case 'evaluate':
        return Target;
      case 'outreach':
        return Mail;
      case 'schedule':
        return Calendar;
      default:
        return CheckCircle2;
    }
  };

  // Handle phase complete
  const handlePhaseComplete = (phase, result) => {
    // Update phase status
    const phaseMap = {
      'source': 'source',
      'evaluate': 'evaluate',
      'outreach': 'outreach',
      'schedule': 'schedule'
    };

    const phaseKey = phaseMap[phase];
    if (phaseKey) {
      setPhaseStatus(prev => ({
        ...prev,
        [phaseKey]: 'done'
      }));
    }

    // Move to next phase
    setCurrentPhase(prev => Math.min(prev + 1, totalPhases));
  };

  // Scroll activity feed to bottom
  useEffect(() => {
    activityFeedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activityFeed]);

  const [pauseError, setPauseError] = useState(null);

  // Handle pause/resume
  const handlePauseResume = async () => {
    if (!goalId) return;
    setPauseError(null);
    if (campaignStatus === 'executing') {
      try {
        const res = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/${goalId}/pause`, { method: 'POST' });
        if (res.ok) {
          setCampaignStatus('paused');
        } else {
          const data = await res.json().catch(() => ({}));
          setPauseError(data.error || 'Failed to pause campaign. Please try again.');
        }
      } catch (e) {
        setPauseError('Failed to pause campaign. Please try again.');
      }
    } else {
      try {
        const res = await fetch(`${API_BASE_URL}/api/campaigns/autonomous/${goalId}/resume`, { method: 'POST' });
        if (res.ok) {
          setCampaignStatus('executing');
        } else {
          const data = await res.json().catch(() => ({}));
          setPauseError(data.error || 'Failed to resume campaign. Please try again.');
        }
      } catch (e) {
        setPauseError('Failed to resume campaign. Please try again.');
      }
    }
  };

  // Get phase status display
  const getPhaseStatusDisplay = (phase) => {
    const status = phaseStatus[phase];
    if (status === 'done') {
      return { text: 'Done', color: 'text-green-600', bg: 'bg-green-100' };
    } else if (status === 'active') {
      return { text: 'Active', color: 'text-blue-600', bg: 'bg-blue-100' };
    } else {
      return { text: 'Next', color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/plan`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
              >
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Campaign Progress</h1>
                <p className="text-sm text-gray-600 mt-1">Real-time monitoring of campaign execution</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                campaignStatus === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : campaignStatus === 'paused'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {campaignStatus === 'completed' ? 'Completed' : campaignStatus === 'paused' ? 'Paused' : 'Executing'}
              </span>
              <span className="text-sm text-gray-600">
                Phase {currentPhase} of {totalPhases}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Campaign Status:</span>
              <span className={`text-sm font-medium ${
                campaignStatus === 'completed' ? 'text-green-600' :
                campaignStatus === 'paused' ? 'text-yellow-600' :
                'text-blue-600'
              }`}>
                {campaignStatus === 'completed' ? '✅ Completed' :
                 campaignStatus === 'paused' ? '⏸ Paused' :
                 '🟢 Executing'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePauseResume}
                disabled={!goalId || campaignStatus === 'completed'}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {campaignStatus === 'paused' ? (
                  <>
                    <Play className="w-4 h-4" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause className="w-4 h-4" />
                    Pause
                  </>
                )}
              </button>
              {pauseError && (
                <span className="text-sm text-red-600" title={pauseError}>{pauseError}</span>
              )}
              <button
                onClick={() => alert('Intervene feature coming soon')}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
              >
                <AlertCircle className="w-4 h-4" />
                Intervene
              </button>
              <button
                onClick={() => alert('Settings feature coming soon')}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Sourced */}
          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-600">Sourced</span>
              </div>
              {phaseStatus.source === 'done' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
              {phaseStatus.source === 'active' && <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>}
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{metrics.sourced}</div>
            <div className={`text-xs font-medium ${getPhaseStatusDisplay('source').color}`}>
              {getPhaseStatusDisplay('source').text}
            </div>
          </div>

          {/* Evaluated */}
          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-600">Evaluated</span>
              </div>
              {phaseStatus.evaluate === 'done' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
              {phaseStatus.evaluate === 'active' && <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>}
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{metrics.evaluated}</div>
            <div className={`text-xs font-medium ${getPhaseStatusDisplay('evaluate').color}`}>
              {getPhaseStatusDisplay('evaluate').text}
            </div>
          </div>

          {/* Contacted */}
          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-600">Contacted</span>
              </div>
              {phaseStatus.outreach === 'done' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
              {phaseStatus.outreach === 'active' && <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>}
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{metrics.contacted}</div>
            <div className={`text-xs font-medium ${getPhaseStatusDisplay('outreach').color}`}>
              {getPhaseStatusDisplay('outreach').text}
            </div>
          </div>

          {/* Scheduled */}
          <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-600">Scheduled</span>
              </div>
              {phaseStatus.schedule === 'done' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
              {phaseStatus.schedule === 'active' && <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>}
            </div>
            <div className="text-3xl font-bold text-orange-600 mb-1">{metrics.scheduled}</div>
            <div className={`text-xs font-medium ${getPhaseStatusDisplay('schedule').color}`}>
              {getPhaseStatusDisplay('schedule').text}
            </div>
          </div>
        </div>

        {/* Overall Progress */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900">Overall Progress</h2>
            <div className="text-sm text-gray-600">
              {progress.timeElapsed} elapsed • {progress.timeRemaining} remaining
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div
              className="bg-orange-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Goal: {progress.goal} scheduled | Current: {progress.current} ({Math.round((progress.current / progress.goal) * 100)}%)
            </span>
            <span className="font-semibold text-gray-900">{progress.percentage}%</span>
          </div>
        </div>

        {/* Current Phase & Activity Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Phase */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Current Phase</h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">
                    Phase {currentPhase}: {currentPhaseDetails.name}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">{currentPhaseDetails.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${currentPhaseDetails.progress}%` }}
                  />
                </div>
              </div>
              <p className="text-sm text-gray-600">{currentPhaseDetails.description}</p>
            </div>
          </div>

          {/* Activity Feed */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Activity Feed</h2>
              <span className="text-xs text-gray-500">Where searched, evaluation, outreach</span>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activityFeed.length > 0 ? (
                activityFeed.map((activity, index) => {
                  const Icon = activity.icon;
                  return (
                    <div key={index} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg transition">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{activity.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Waiting for activity...</p>
                </div>
              )}
              <div ref={activityFeedEndRef} />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/plan`)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Plan
          </button>

          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/campaigns/autonomous/${session_id || 'new'}/results`)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
            >
              {campaignStatus === 'completed' ? 'View Results' : 'View Details'}
            </button>
            <button
              onClick={() => alert('Export feature coming soon')}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => navigate('/campaigns/autonomous/new')}
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Campaign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CampaignProgress;

